import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Modal } from 'react-bootstrap';
import AdminDataTable from './AdminDataTable';
import Swal from 'sweetalert2';
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaList,
  FaSearch,
  FaSignInAlt,
  FaHistory,
  FaArrowUp,
  FaUserFriends,
  FaWhatsapp
} from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import AdminPageHeader from './AdminPageHeader';
import Navigation from './Navigation';

const CODE39_PATTERNS = {
  '0': 'nnnwwnwnn',
  '1': 'wnnwnnnnw',
  '2': 'nnwwnnnnw',
  '3': 'wnwwnnnnn',
  '4': 'nnnwwnnnw',
  '5': 'wnnwwnnnn',
  '6': 'nnwwwnnnn',
  '7': 'nnnwnnwnw',
  '8': 'wnnwnnwnn',
  '9': 'nnwwnnwnn',
  A: 'wnnnnwnnw',
  B: 'nnwnnwnnw',
  C: 'wnwnnwnnn',
  D: 'nnnnwwnnw',
  E: 'wnnnwwnnn',
  F: 'nnwnwwnnn',
  G: 'nnnnnwwnw',
  H: 'wnnnnwwnn',
  I: 'nnwnnwwnn',
  J: 'nnnnwwwnn',
  K: 'wnnnnnnww',
  L: 'nnwnnnnww',
  M: 'wnwnnnnwn',
  N: 'nnnnwnnww',
  O: 'wnnnwnnwn',
  P: 'nnwnwnnwn',
  Q: 'nnnnnnwww',
  R: 'wnnnnnwwn',
  S: 'nnwnnnwwn',
  T: 'nnnnwnwwn',
  U: 'wwnnnnnnw',
  V: 'nwwnnnnnw',
  W: 'wwwnnnnnn',
  X: 'nwnnwnnnw',
  Y: 'wwnnwnnnn',
  Z: 'nwwnwnnnn',
  '-': 'nwnnnnwnw',
  '.': 'wwnnnnwnn',
  ' ': 'nwwnnnwnn',
  '$': 'nwnwnwnnn',
  '/': 'nwnwnnnwn',
  '+': 'nwnnnwnwn',
  '%': 'nnnwnwnwn',
  '*': 'nwnnwnwnn',
};

const sanitizeCode39Value = value =>
  String(value || '')
    .toUpperCase()
    .replace(/[^0-9A-Z\-. $/+%]/g, '')
    .trim();

const slugifyFileName = value =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const drawRoundedRect = (context, x, y, width, height, radius) => {
  const safeRadius = Math.max(0, Math.min(radius, width / 2, height / 2));
  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.lineTo(x + width - safeRadius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  context.lineTo(x + width, y + height - safeRadius);
  context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  context.lineTo(x + safeRadius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  context.lineTo(x, y + safeRadius);
  context.quadraticCurveTo(x, y, x + safeRadius, y);
  context.closePath();
};

const fitText = (context, text, maxWidth, maxFontSize, minFontSize, fontWeight = 700) => {
  for (let fontSize = maxFontSize; fontSize >= minFontSize; fontSize -= 2) {
    context.font = `${fontWeight} ${fontSize}px Arial`;
    if (context.measureText(text).width <= maxWidth) {
      return fontSize;
    }
  }
  return minFontSize;
};

const drawCode39Barcode = (context, value, x, y, width, height) => {
  const encoded = `*${sanitizeCode39Value(value)}*`;
  let totalUnits = 0;

  for (let index = 0; index < encoded.length; index += 1) {
    const pattern = CODE39_PATTERNS[encoded[index]];
    if (!pattern) {
      continue;
    }

    for (let patternIndex = 0; patternIndex < pattern.length; patternIndex += 1) {
      totalUnits += pattern[patternIndex] === 'w' ? 3 : 1;
    }

    if (index < encoded.length - 1) {
      totalUnits += 1;
    }
  }

  if (!totalUnits) {
    return;
  }

  const narrowUnit = width / totalUnits;
  const wideUnit = narrowUnit * 3;
  let cursorX = x;

  for (let index = 0; index < encoded.length; index += 1) {
    const pattern = CODE39_PATTERNS[encoded[index]];
    if (!pattern) {
      continue;
    }

    for (let patternIndex = 0; patternIndex < pattern.length; patternIndex += 1) {
      const isBar = patternIndex % 2 === 0;
      const segmentWidth = pattern[patternIndex] === 'w' ? wideUnit : narrowUnit;
      if (isBar) {
        context.fillRect(cursorX, y, segmentWidth, height);
      }
      cursorX += segmentWidth;
    }

    if (index < encoded.length - 1) {
      cursorX += narrowUnit;
    }
  }
};

const buildMemberCardImage = ({ memberName, barcodeValue, outletName }) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 720;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas tidak tersedia');
  }

  const background = context.createLinearGradient(0, 0, canvas.width, canvas.height);
  background.addColorStop(0, '#0f4c5c');
  background.addColorStop(1, '#1f8a70');
  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);

  context.fillStyle = 'rgba(255, 255, 255, 0.08)';
  context.beginPath();
  context.arc(1080, 110, 180, 0, Math.PI * 2);
  context.fill();
  context.beginPath();
  context.arc(140, 620, 160, 0, Math.PI * 2);
  context.fill();

  drawRoundedRect(context, 70, 70, 1060, 580, 36);
  context.fillStyle = '#f7f5ef';
  context.fill();

  context.fillStyle = '#1f8a70';
  context.font = '700 26px Arial';
  context.fillText('MEMBER CARD', 120, 140);

  context.fillStyle = '#10343d';
  const fittedNameSize = fitText(context, memberName, 620, 52, 30);
  context.font = `700 ${fittedNameSize}px Arial`;
  context.fillText(memberName, 120, 220);

  context.fillStyle = '#58707a';
  context.font = '400 24px Arial';
  context.fillText(`Barcode ID: ${barcodeValue}`, 120, 268);
  context.fillText(`Outlet: ${outletName || 'Semua Outlet'}`, 120, 306);

  drawRoundedRect(context, 120, 350, 960, 210, 28);
  context.fillStyle = '#ffffff';
  context.fill();

  context.fillStyle = '#111111';
  drawCode39Barcode(context, barcodeValue, 180, 390, 840, 110);

  context.fillStyle = '#111111';
  context.font = '700 34px Arial';
  context.textAlign = 'center';
  context.fillText(barcodeValue, 600, 545);
  context.textAlign = 'start';

  context.fillStyle = '#6c7c84';
  context.font = '400 20px Arial';
  context.fillText('Upload gambar ini ke WhatsApp Web untuk mengirim kartu member.', 120, 612);

  return canvas.toDataURL('image/png');
};

function Members() {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(0);

  const [paymentsmethode, setPaymentsmethode] = useState([]);

  // ===== DAILY PAYMENT STATE =====
  const [showDaily, setShowDaily] = useState(false);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyMember, setDailyMember] = useState(null);

  const [dailyForm, setDailyForm] = useState({
    payment_method: '',
    qty: 1,
  });

  // ===== HISTORY STATE =====
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [membershipHistory, setMembershipHistory] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);

  const navigate = useNavigate();
  const openMembershipSalesDesk = useCallback(
    member => {
      const params = new URLSearchParams();
      params.set('tab', 'membership');
      if (member?.outlet) {
        params.set('outlet_id', String(member.outlet));
      }
      if (member?.id) {
        params.set('member_id', String(member.id));
      }
      const memberName = `${member?.first_name || ''} ${member?.last_name || ''}`.trim();
      if (memberName) {
        params.set('member_name', memberName);
      }
      navigate(`/fitness-transactions?${params.toString()}`);
    },
    [navigate]
  );
  const formatWhatsappNumber = hp => {
    if (!hp) return '';
      let number = hp.replace(/[^0-9]/g, ''); // buang spasi / simbol

      if (number.startsWith('0')) {
        number = '62' + number.slice(1);
      }
      return number;
  };

  const formatMemberFullName = member =>
    `${member?.first_name || ''} ${member?.last_name || ''}`.trim() || 'Member';

  const formatMemberBarcodeValue = member => {
    const preferredValue = String(member?.id || '').trim();
    if (preferredValue) {
      return preferredValue;
    }

    return String(member?.reff_code || '').trim();
  };

  const handleSendWhatsappCard = member => {
    const phone = formatWhatsappNumber(member.hp);
    if (!phone) {
      Swal.fire('Warning', 'Nomor WhatsApp tidak tersedia', 'warning');
      return;
    }

    const memberName = formatMemberFullName(member);
    const memberBarcodeValue = sanitizeCode39Value(formatMemberBarcodeValue(member));
    if (!memberBarcodeValue) {
      Swal.fire('Warning', 'Barcode ID member tidak tersedia', 'warning');
      return;
    }

    const outletName = outlets.find(item => item.ID === member.outlet)?.OutletName || '';

    try {
      const cardDataUrl = buildMemberCardImage({
        memberName,
        barcodeValue: memberBarcodeValue,
        outletName,
      });

      const downloadLink = document.createElement('a');
      const safeName = slugifyFileName(memberName) || `member-${memberBarcodeValue}`;
      downloadLink.href = cardDataUrl;
      downloadLink.download = `${safeName}-member-card.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      const message = encodeURIComponent(
        [
          `Halo ${memberName},`,
          '',
          'Berikut kartu member Anda.',
          'Silakan simpan gambar barcode terlampir untuk kebutuhan check-in.',
        ].join('\n')
      );

      window.open(
        `https://web.whatsapp.com/send?phone=${phone}&text=${message}`,
        '_blank'
      );

      Swal.fire(
        'Kartu siap dikirim',
        'Gambar kartu member sudah terunduh. Setelah WhatsApp Web terbuka, upload file PNG yang baru diunduh.',
        'success'
      );
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Gagal menyiapkan kartu member', 'error');
    }
  };

const fetchPaymentsMethode = useCallback(async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/getassetaccounts`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
        },
      }
    );

    const data = await response.json();
    if (data.error === 0 || data.error === '0') {
      setPaymentsmethode(data.results);
    }
  } catch (error) {
    console.error(error);
  }
}, []);


  // ================= FETCH MEMBERS =================
  const fetchMembers = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}member/list`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ limit: 500, offset: 0, outlet: 0 }),
        }
      );

      const data = await response.json();
      if (data.error === 0 && Array.isArray(data.results)) {
        setMembers(data.results);
      } else {
        setMembers([]);
        Swal.fire('Error', 'Failed to fetch member data', 'error');
      }
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Failed to fetch member data', 'error');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // ================= FETCH OUTLETS =================
  const fetchOutlets = useCallback(async () => {
    const token = localStorage.getItem('sessionToken');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}outletlist`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ limit: 100, offset: 0 }),
        }
      );

      const data = await response.json();
      if (data.error === '0' && Array.isArray(data.results)) {
        setOutlets(
          data.results.map(o => ({
            ID: o.id,
            OutletName: o.outlet_name,
          }))
        );
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
  fetchOutlets();
  fetchMembers();
  fetchPaymentsMethode();
}, [fetchOutlets, fetchMembers, fetchPaymentsMethode]);

  // ================= FILTER =================
  useEffect(() => {
    let filtered = members;

    if (search.trim()) {
      filtered = filtered.filter(m =>
        `${m.first_name} ${m.last_name}`
          .toLowerCase()
          .includes(search.toLowerCase())
      );
    }

    if (selectedOutlet !== 0) {
      filtered = filtered.filter(m => m.outlet === selectedOutlet);
    }

    setFilteredMembers(filtered);
  }, [members, search, selectedOutlet]);

  // ================= CHECK-IN =================
  const fetchCheckin = useCallback(async member => {
    const token = localStorage.getItem('sessionToken');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}admin/checkin`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            member_id: parseInt(member.id),
            outlet_id: parseInt(member.outlet),
          }),
        }
      );

      const data = await response.json();

      const memberName = `${member.first_name} ${member.last_name}`.trim();
      window.dispatchEvent(
        new CustomEvent('primefit:checkin-toast', {
          detail: {
            member: memberName,
            status: data.error === '0' ? 'success' : 'failed',
            statusMessage: data.message || '',
          },
        })
      );

      if (data.error === '0') {
        Swal.fire('Success', 'Member checked-in successfully', 'success');
      } else {
        Swal.fire('Error', data.message || 'Check-in failed', 'error');
      }
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Check-in failed', 'error');
    }
  }, []);

  const handleCheckin = async member => {
    const confirm = await Swal.fire({
      title: 'Check-in Member?',
      text: `${member.first_name} ${member.last_name}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Check-in',
    });

    if (!confirm.isConfirmed) return;
    fetchCheckin(member);
  };

  // ================= MEMBERSHIP HISTORY (GET) =================
  const fetchMembershipHistory = useCallback(async member => {
  const token = localStorage.getItem('sessionToken');
  setSelectedMember(member);
  setHistoryLoading(true);

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/admin/membershippaymenthistory?id=${member.id}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response.json();
    if (data.error === 0 && data.results) {
      // ⬇️ PENTING: paksa jadi array
      const resultsArray = Array.isArray(data.results)
        ? data.results
        : [data.results];

      setMembershipHistory(resultsArray);
      setShowHistory(true);
    } else {
      setMembershipHistory([]);
      Swal.fire('Info', 'No membership history found', 'info');
    }
  } catch (error) {
    Swal.fire('Error', 'Failed to load history', 'error');
  } finally {
    setHistoryLoading(false);
  }
}, []);


  // ================= DELETE =================
  const handleDelete = useCallback(async id => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
    });

    if (!result.isConfirmed) return;

    const token = localStorage.getItem('sessionToken');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}member/delete`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id }),
        }
      );

      const data = await response.json();
      if (data.error === 0) {
        Swal.fire('Deleted!', 'Member has been deleted.', 'success');
        setMembers(prev => prev.filter(m => m.id !== id));
      } else {
        Swal.fire('Error', 'Failed to delete member.', 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to delete member.', 'error');
    }
  }, []);

  // ================= TABLE COLUMNS =================
  const columns = [
    { name: 'ID', selector: row => row.id, width: '80px', sortable: true },
    {
      name: 'Member',
      sortable: true,
      grow: 2,
      cell: row => (
        <div className="table-identity">
          <span className="table-identity__avatar">
            {`${row.first_name || ''}${row.last_name || ''}`.trim().slice(0, 2).toUpperCase() || 'MB'}
          </span>
          <span className="table-identity__meta">
            <strong>{row.first_name} {row.last_name}</strong>
            <small>{row.email || row.hp || 'No contact yet'}</small>
          </span>
        </div>
      ),
    },
    {
      name: 'Outlet',
      cell: row => {
        const outlet = outlets.find(o => o.ID === row.outlet);
        return (
          <span className="table-identity__meta">
            <strong>{outlet ? outlet.OutletName : '-'}</strong>
            <small>{row.hp || 'No phone number'}</small>
          </span>
        );
      },
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="table-action-group">
          <Button size="sm" variant="outline-success" onClick={() => handleCheckin(row)}>
            <FaSignInAlt />
          </Button>

          <Button
            size="sm"
            variant="outline-info"
            onClick={() => fetchMembershipHistory(row)}
          >
            <FaHistory />
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => openMembershipSalesDesk(row)}
          >
            <FaArrowUp />
          </Button>
          <Button
            size="sm"
            variant="outline-warning"
            onClick={() => navigate(`/edit-member/${row.id}`)}
          >
            <FaEdit />
          </Button>

          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => handleDelete(row.id)}
          >
            <FaTrash />
          </Button>
          <Button
          size="sm"
          variant="outline-success"
          onClick={() => handleSendWhatsappCard(row)}
        >
          <FaWhatsapp />
        </Button>

        </div>
      ),
      width: '200px',
      right: true,
    },
  ];

  return (
    <div>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaUserFriends}
          eyebrow="Client lifecycle"
          title="Client Management"
          description="Pantau client aktif, proses kehadiran, upgrade paket, dan buka riwayat transaksi dengan lebih cepat."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Clients' },
          ]}
          actions={(
            <>
              <Button
                size="sm"
                onClick={() => {
                  setDailyMember(39);
                  setDailyForm({
                    payment_method: '',
                    qty: 1,
                  });
                  setShowDaily(true);
                }}
              >
                Daily Pass
              </Button>

              <Button onClick={() => navigate('/add-member')}>
                <FaPlus /> Add Client
              </Button>
            </>
          )}
          meta={(
            <span className="soft-stat">
              <FaList /> {filteredMembers.length} clients
            </span>
          )}
        />

        <div className="section-card table-card">
          <div className="table-toolbar">
            <span className="status-chip">
              <FaList /> {filteredMembers.length} members
            </span>
            <div className="page-actions">
              <Form.Select
                className="filter-control"
                value={selectedOutlet}
                onChange={e => setSelectedOutlet(parseInt(e.target.value))}
              >
                <option value={0}>All Outlets</option>
                {outlets.map(o => (
                  <option key={o.ID} value={o.ID}>{o.OutletName}</option>
                ))}
              </Form.Select>

              <div className="control-with-icon search-control">
                <span className="control-with-icon__icon">
                  <FaSearch />
                </span>
                <Form.Control
                  placeholder="Search member..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="card-body pt-0">
            <AdminDataTable
              columns={columns}
              data={filteredMembers}
              pagination
              progressPending={loading}
              highlightOnHover
              striped
              responsive
              emptyTitle="No member data found"
              emptyDescription="Tambahkan member baru atau ubah filter outlet untuk melihat data."
            />
          </div>
        </div>
      </Container>
      {/* ===== MODAL HISTORY ===== */}
      <Modal show={showHistory} onHide={() => setShowHistory(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Membership History
            {selectedMember && (
              <small className="d-block text-muted">
                {selectedMember.first_name} {selectedMember.last_name}
              </small>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {historyLoading ? (
            <p>Loading...</p>
          ) : membershipHistory.length === 0 ? (
            <p className="text-muted text-center">No history found</p>
          ) : (
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Outlet ID</th>
                  <th>Membership</th>
                  <th>Amount</th>
                  <th>Created</th>
                  <th>Expired</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {membershipHistory.map((h, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{h.outlet}</td>
                    <td>{h.membershipname || h.type || '-'}</td>
                    <td>{h.amount?.toLocaleString('id-ID') || 0}</td>
                    <td>
                      {h.created_at
                        ? new Date(h.created_at).toLocaleString('id-ID')
                        : '-'}
                    </td>
                    <td>
                      {h.expired
                        ? new Date(h.expired).toLocaleString('id-ID')
                        : '-'}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          h.status === 1 ? 'bg-success' : 'bg-secondary'
                        }`}
                      >
                        {h.status === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistory(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {/* ===== MODAL MEMBER HARIAN ===== */}
      <Modal show={showDaily} onHide={() => setShowDaily(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Member Harian</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {dailyMember && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Metode Pembayaran</Form.Label>
                <Form.Select
                  value={dailyForm.payment_method}
                  onChange={e =>
                    setDailyForm(prev => ({
                      ...prev,
                      payment_method: e.target.value,
                    }))
                  }
                >
                  <option value="">-- Select Payment Method --</option>
                  {paymentsmethode.map(p => (
                    <option key={p.AccountID} value={p.AccountID}>
                      {p.AccountName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Jumlah</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={dailyForm.qty}
                  onChange={e =>
                    setDailyForm(prev => ({
                      ...prev,
                      qty: Number(e.target.value),
                    }))
                  }
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDaily(false)}>
            Cancel
          </Button>

          <Button
            variant="primary"
            disabled={dailyLoading}
            onClick={async () => {
              if (!dailyForm.payment_method || dailyForm.qty < 1) {
                Swal.fire('Warning', 'Lengkapi data terlebih dahulu', 'warning');
                return;
              }

              setDailyLoading(true);
              const token = localStorage.getItem('sessionToken');

              try {
                for (let i = 0; i < dailyForm.qty; i++) {
                  const response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}upgradememberadmin`,
                    {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                      },
                      body: JSON.stringify({
                        outlet: Number(dailyMember.outlet),
                        member_id: 39,
                        membership_id: 6, // khusus harian (sesuai backend)
                        payment_methode: dailyForm.payment_method,
                        transaction_source: 'daily_pass',
                      }),
                    }
                  );

                  const data = await response.json();
                  if (data.error !== 0) {
                    throw new Error(data.message || 'Gagal submit');
                  }
                }

                Swal.fire('Success', 'Member harian berhasil ditambahkan', 'success');
                setShowDaily(false);
                setTimeout(() => navigate('/membership-payment'), 1000);
              } catch (err) {
                Swal.fire('Error', err.message || 'Proses gagal', 'error');
              } finally {
                setDailyLoading(false);
              }
            }}
          >
            Submit
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
}

export default Members;
